'use client';

import { Stack } from '@castlery/fortress';
import { useTrackingTags } from '@castlery/modules-tracking-components';
import { AdvancedVideo } from '@cloudinary/react';
import { Cloudinary } from '@cloudinary/url-gen';
import { fill } from '@cloudinary/url-gen/actions/resize';
import { storyblokEditable } from '@storyblok/react/rsc';
import debounce from 'lodash.debounce';
import { useMemo, useState } from 'react';
import { extractPublicIdFromUrl } from '../image/cloudinary-image';
import { useDevice } from '../image/image';

export type VideoProps = {
  blok: {
    _uid?: string;
    desktop_url?: string;
    tablet_url?: string;
    mobile_url?: string;
    desktopUrl?: string;
    tabletUrl?: string;
    mobileUrl?: string;
    autoplay?: boolean;
    controls?: boolean;
  };
  loader?: {
    [key: string]: any;
  };
  resize?: {
    [key: string]: any;
  };
};

type VideoUrls = {
  [key: string]: string | undefined;
};

const VideoWrapper = ({ blok, loader = {}, resize, videoWidth, videoHeight }: VideoProps) => {
  const device = useDevice();
  const desktop = device === 'desktop';
  const cld = useMemo(
    () =>
      new Cloudinary({
        cloud: {
          cloudName: 'castlery',
        },
      }),
    []
  );
  const { _uid, desktop_url, tablet_url, mobile_url, autoplay, controls, desktopUrl, mobileUrl, tabletUrl } =
    blok || {};
  const { ratio } = loader;

  const videoUrl: VideoUrls = {
    mobile: mobile_url || mobileUrl,
    tablet: tablet_url || mobile_url || tabletUrl || mobileUrl,
    desktop: desktop_url || desktopUrl,
  };
  const width = useMemo(() => (!desktop ? 1280 : 1920), [desktop]);
  const [watchCompleted, setWatchCompleted] = useState(false);

  const myVideo = useMemo(() => {
    const publicId = extractPublicIdFromUrl(videoUrl[device]);
    const v = cld.video(publicId);
    if (ratio > 0) {
      v.resize(fill().aspectRatio(desktop ? 1 / ratio : ratio)).format('auto');
    } else {
      v.resize(fill().width(width)).format('auto');
    }
    return v;
  }, [cld, device, videoUrl.desktop, videoUrl.tablet, videoUrl.mobile, ratio, width]);

  const debouncedTrack = debounce((percentage) => {
    if (watchCompleted) return;
    if (percentage === 100) {
      setWatchCompleted(true);
    }
    if (window?.dataLayer) {
      window.dataLayer.push({
        event: 'trackEvent',
        'eventDetails.category': 'Storyblok',
        'eventDetails.action': 'image_video view',
        'eventDetails.position': `${percentage}% Completion`,
        'eventDetails.label': videoUrl?.[device],
      });
    }
  }, 1000);

  const trackingTags = useTrackingTags({
    moduleName: 'general-video',
    elementName: 'video',
    content: {
      assetLink: videoUrl?.[device],
    },
  });

  return (
    <Stack
      {...storyblokEditable(blok)}
      key={_uid}
      sx={() => ({
        width: videoWidth,
        height: videoHeight,
        video: {
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        },
      })}
    >
      {/* <ReactPlayer
        className={mediaVideoClasses.player}
        url={videoUrl?.[device] || ''}
        muted={true}
        playing={true}
        loop={true}
        controls={false}
        playsinline={true}
        stopOnUnmount={true}
        width="100%"
        height="100%"
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
              'data-dt-id': JSON.stringify(trackingTags)
            },
          },
        }}
      /> */}
      <AdvancedVideo
        onTimeUpdate={(event) => {
          if (event.target instanceof HTMLVideoElement) {
            const percentage = Math.round((event.target.currentTime / event.target.duration) * 100);
            if ([25, 50, 75, 100].includes(percentage)) {
              debouncedTrack(percentage);
            }
          }
        }}
        cldVid={myVideo}
        controls={controls}
        muted
        loop
        autoPlay={autoplay}
        playsInline
      />
    </Stack>
  );
};

export { VideoWrapper };
